type CurrencyPosition = 'prefix' | 'prefix-space' | 'suffix';

interface CurrencyConfig {
  symbol: string;
  locale: string;
  position: CurrencyPosition;
}

const currencyConfigs: Record<string, CurrencyConfig> = {
  USD: { symbol: '$', locale: 'en-US', position: 'prefix' },
  EUR: { symbol: '€', locale: 'de-DE', position: 'suffix' },
  UAH: { symbol: 'UAH', locale: 'en-US', position: 'prefix-space' },
  PLN: { symbol: 'zł', locale: 'pl-PL', position: 'suffix' },
  RUB: { symbol: '₽', locale: 'ru-RU', position: 'suffix' },
  SEK: { symbol: 'kr', locale: 'sv-SE', position: 'suffix' },
};

const sanitizeCurrencyName = (currencyName?: string | null): string | undefined => {
  if (!currencyName) {
    return undefined;
  }

  const normalized = currencyName.trim().toUpperCase();

  if (!normalized || normalized === 'NULL' || normalized === 'NONE' || normalized === 'N/A' || normalized === 'UNDEFINED') {
    return undefined;
  }

  return normalized;
};

export const getCurrencySymbol = (currencyName?: string | null): string => {
  const normalized = sanitizeCurrencyName(currencyName) ?? 'USD';
  const config = currencyConfigs[normalized];

  if (config) {
    return config.symbol;
  }

  if (normalized === 'USD') {
    return '$';
  }

  return normalized;
};

const formatWithConfig = (amount: number, config: CurrencyConfig): string => {
  const formattedValue = amount.toLocaleString(config.locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (config.position === 'prefix') {
    return `${config.symbol}${formattedValue}`;
  }

  if (config.position === 'prefix-space') {
    return `${config.symbol} ${formattedValue}`;
  }

  return `${formattedValue} ${config.symbol}`;
};

const formatAsUsd = (amount: number): string => {
  return formatWithConfig(amount, currencyConfigs.USD);
};

export const formatCurrency = (amount: number, currencyName?: string | null): string => {
  try {
    const normalized = sanitizeCurrencyName(currencyName);

    if (!normalized) {
      return formatAsUsd(amount);
    }

    const config = currencyConfigs[normalized];
    if (config) {
      return formatWithConfig(amount, config);
    }

    const formattedValue = amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `${normalized} ${formattedValue}`;
  } catch (error) {
    console.warn('Failed to format currency, defaulting to USD.', { amount, currencyName, error });
    return formatAsUsd(amount);
  }
};

export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};
